import axios from 'axios';
import { load } from 'cheerio';
import config from '../config';
import logger from '../utils/logger';
import { setCache, getCache, cleanup } from '../utils/redisClient';

class ScraperService {
  constructor() {
    this.baseUrl = 'https://www.crazygames.com';
    this.isRunning = false;
    this.interval = null;
    console.log('ScraperService initialized');
    // 创建带有更新配置的 axios 实例，移除不安全的请求头
    this.axiosInstance = axios.create({
      timeout: 30000,
      headers: {
        'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        'Accept-Language': 'en-US,en;q=0.5',
        'Cache-Control': 'no-cache'
      },
      withCredentials: false,
      maxRedirects: 5
    });

    // 添加重试逻辑
    this.axiosInstance.interceptors.response.use(undefined, async (err) => {
      const { config } = err;
      if (!config || !config.retry) return Promise.reject(err);
      
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount >= config.retry) return Promise.reject(err);
      
      config.__retryCount += 1;
      console.log(`Retrying request (${config.__retryCount}/${config.retry})`);
      await this.delay(1000 * config.__retryCount);
      return this.axiosInstance(config);
    });

    console.log('ScraperService initialized');
  }

  // 开始定时抓取
  startAutoScraping(intervalHours = 24) {
    if (this.isRunning) {
      console.log('Scraper is already running');
      return;
    }

    this.isRunning = true;
    console.log(`Starting auto scraping with ${intervalHours} hour interval`);

    // 立即执行一次
    this.runScraper();

    // 设置定时任务
    this.interval = setInterval(() => {
      this.runScraper();
    }, intervalHours * 60 * 60 * 1000);
  }

  // 停止定时抓取
  stopAutoScraping() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.isRunning = false;
    console.log('Auto scraping stopped');
  }

  // 主抓取流程
  async runScraper() {
    try {

      console.log('Starting scraping process...');
      const categories = await this.scrapeCategories();
      console.log('Categories scraped:', categories);

      const allGames = [];
      for (const category of categories) {
        const games = await this.scrapeGames(category);
        allGames.push(...games);
        console.log(`Scraped ${games.length} games for ${category.name}`);
        await this.delay(2000); // 添加延迟
      }

      return { categories, games: allGames };
    } catch (error) {
      console.error('Error in runScraper:', error);
      throw error;
    }
  }

  // 抓取分类
  async scrapeCategories() {
    try {
      console.log('Checking Redis cache for categories...');
      // cleanup();
      // const cachedCategories = await getCache('categories');
      
      // if (cachedCategories) {
      //   console.log('Using cached categories');
      //   return cachedCategories;
      // }

      console.log('Fetching categories from website...');
      const response = await this.axiosInstance.get(
        `${config.PROXY_URL}/`,
        { retry: 3 }
      );

      const $ = load(response.data);
      const categories = [];

      // 根据网站结构更新选择器
      $('nav a').each((i, el) => {
        const $el = $(el);
        const text = $el.text().trim();
        const href = $el.attr('href');
        
        // 记录每个找到的元素
        logger.info(`Found nav item: ${text} ; href --${href}`);
        
        // 如果遇到 Tags，跳出循环
        if (text === 'Tags') {
          logger.info('Found Tags, stopping category collection');
          return false;
        }
        
        // 只处理有效的分类
        if (text && (!text.includes('Recently played') && !text.includes('New')
          && !text.includes('Originals'))) {
          const category = {
            name: text,
            href: href,
            slug: text.toLowerCase().replace(/\s+/g, '-'),
            icon: '🎮',
            order: i,
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          categories.push(category);
          logger.info(`Added category: ${JSON.stringify(category)}`);
        }
      });

      console.log(`Found ${categories.length} categories`);
      
      // 存储到 Redis，设置 24 小时过期
      await setCache('categories', categories, 24 * 3600);
      console.log('Categories cached in Redis');

      return categories;
    } catch (error) {
      console.error('Error scraping categories:', error);
      throw error;
    }
  }

  // 抓取游戏
  async scrapeGames(category) {
    try {
      // 检查缓存
      const cacheKey = `games_${category._id}`;
      // const cachedGames = await getCache(cacheKey);
      
      // if (cachedGames) {
      //   console.log(`Using cached games for category ${category.name}`);
      //   return cachedGames;
      // }

       
      console.log(`Fetching games for category: ${category.name}`);
      console.log(`Fetching games for href: ${category.href}`);
      const response = await this.axiosInstance.get(category.href, { 
        retry: 3,
        headers: {
          'Referer': this.baseUrl,
          'Origin': this.baseUrl
        }
      });

      const $ = load(response.data);
      const games = [];

      // 更新游戏卡片选择器
      $('.game-list-item, .game-grid-item').each((i, el) => {
        const $el = $(el);
        const title = $el.find('.game-title, .game-grid-item-title').text().trim();
        const href = $el.find('a').attr('href');
        const image = $el.find('img').attr('src') || $el.find('img').attr('data-src');
        const description = $el.find('.game-description').text().trim();
        const playCount = $el.find('.play-count').text().trim();

        if (title && href) {
          games.push({
            title,
            slug: href.split('/').pop(),
            gameUrl: `${this.baseUrl}${href}`,
            image,
            description,
            playCount: playCount ? parseInt(playCount.replace(/[^0-9]/g, '')) : 0,
            category: category._id,
            categoryName: category.name,
            categorySlug: category.slug,
            _id: `game_${category._id}_${i + 1}`,
            isPublished: true
          });
        }
      });

      console.log(`Found ${games.length} games in category ${category.name}`);
      
      // 存储到 Redis，设置 12 小时过期
      // await setCache(cacheKey, games, 12 * 3600);
      console.log(`Games for category ${category.name} cached in Redis`);

      return games;
    } catch (error) {
      console.error(`Error scraping games for ${category.name}:`, error);
      throw error;
    }
  }

  // 延迟函数
  async delay(ms) {
    const randomMs = Math.floor(Math.random() * (ms * 0.2)) + ms;
    return new Promise(resolve => setTimeout(resolve, randomMs));
  }
}

export default new ScraperService(); 